/** Owns the client half of the variant badge contract. */

const FALLBACK_TIER = 3;
const NO_WINNER_TIER = 99;
const MAX_IMAGE_BADGES = 2;

const finiteNumber = (value) => {
  if (value === '' || value === null || typeof value === 'undefined') return null;
  const parsedValue = Number(value);
  return Number.isFinite(parsedValue) ? parsedValue : null;
};

const isWithinSchedule = (startValue, endValue, now, requireBound = false) => {
  const start = finiteNumber(startValue);
  const end = finiteNumber(endValue);
  if (requireBound && start === null && end === null) return false;
  return (start === null || now >= start) && (end === null || now < end);
};

const waitlistWindows = (scheduleValue) => {
  if (!scheduleValue) return [];
  try {
    const windows = JSON.parse(scheduleValue);
    return Array.isArray(windows) ? windows.filter((window) => Array.isArray(window)) : [];
  } catch (_error) {
    return [];
  }
};

const isWithinAnySchedule = (scheduleValue, now) =>
  waitlistWindows(scheduleValue).some(([start, end]) => isWithinSchedule(start, end, now, true));

const tierFor = (rule, scheduled) => {
  if (rule === 'waitlist') return 1;
  if (rule === 'out-of-stock') return 2;
  return scheduled ? 4 : 5;
};

const isApplicable = (rule, { available, waitlistActive }) => {
  if (rule === 'waitlist') return waitlistActive;
  if (rule === 'out-of-stock') return !available;
  return true;
};

class ProductBadgeGroup extends HTMLElement {
  connectedCallback() {
    this.refresh = this.refresh.bind(this);
    this.handleVisibilityChange = this.handleVisibilityChange.bind(this);

    window.addEventListener('pageshow', this.refresh);
    document.addEventListener('visibilitychange', this.handleVisibilityChange);
    this.refresh();
  }

  disconnectedCallback() {
    window.clearTimeout(this.scheduleTimer);
    window.removeEventListener('pageshow', this.refresh);
    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
  }

  handleVisibilityChange() {
    if (document.hidden) return;
    this.refresh();
  }

  get badges() {
    return Array.from(this.querySelectorAll('[data-product-badge]'));
  }

  get fallbackBadge() {
    return this.querySelector('[data-product-badge-fallback]');
  }

  /** Re-runs the race and shows the single winning badge. */
  refresh() {
    const now = Math.floor(Date.now() / 1000);
    const available = this.dataset.variantAvailable !== 'false';
    const waitlistActive = isWithinAnySchedule(this.dataset.waitlistSchedule, now);

    this.dataset.waitlistActive = String(waitlistActive);

    const state = { available, waitlistActive };
    const candidates = this.badges.map((badge) => {
      const rule = badge.dataset.badgeRule || '';
      const scheduled = badge.dataset.badgeScheduled === 'true';
      const eligible =
        isApplicable(rule, state) &&
        isWithinSchedule(badge.dataset.badgeStart, badge.dataset.badgeEnd, now);

      return { badge, eligible, tier: eligible ? tierFor(rule, scheduled) : NO_WINNER_TIER };
    });

    const fallback = this.fallbackBadge;
    const fallbackEligible = Boolean(fallback) && !available;

    let bestTier = candidates.reduce(
      (best, entry) => (entry.eligible ? Math.min(best, entry.tier) : best),
      NO_WINNER_TIER
    );
    if (fallbackEligible && FALLBACK_TIER < bestTier) bestTier = FALLBACK_TIER;

    let winnerFound = false;
    const showFallback = fallbackEligible && bestTier === FALLBACK_TIER;
    if (showFallback) winnerFound = true;
    if (fallback) fallback.hidden = !showFallback;

    const isImageGroup = this.dataset.badgeKind === 'image';
    let imageBadgesShown = 0;

    candidates.forEach((entry) => {
      const wins = isImageGroup
        ? entry.eligible && imageBadgesShown++ < MAX_IMAGE_BADGES
        : !winnerFound && entry.eligible && entry.tier === bestTier;
      if (wins) winnerFound = true;
      entry.badge.hidden = !wins;
    });

    this.hidden = !winnerFound;
    this.dataset.hasWinner = String(winnerFound);
    this.dispatchEvent(
      new CustomEvent('dieux:product-badge-resolved', {
        bubbles: true,
        detail: { hasWinner: winnerFound },
      })
    );
    this.scheduleNextBoundary(now);
  }

  /** Wakes the group exactly when the next schedule bound would change the winner. */
  scheduleNextBoundary(now) {
    window.clearTimeout(this.scheduleTimer);

    const boundaries = [];
    const addBoundary = (value) => {
      const boundary = finiteNumber(value);
      if (boundary !== null && boundary > now) boundaries.push(boundary);
    };

    this.badges.forEach((badge) => {
      addBoundary(badge.dataset.badgeStart);
      addBoundary(badge.dataset.badgeEnd);
    });
    waitlistWindows(this.dataset.waitlistSchedule).forEach(([start, end]) => {
      addBoundary(start);
      addBoundary(end);
    });

    if (boundaries.length === 0) return;

    const nextBoundary = Math.min(...boundaries);
    const delay = Math.min(Math.max((nextBoundary - now) * 1000 + 50, 50), 2147483000);
    this.scheduleTimer = window.setTimeout(this.refresh, delay);
  }
}

if (!customElements.get('product-badge-group')) {
  customElements.define('product-badge-group', ProductBadgeGroup);
}

export { ProductBadgeGroup };
